import { useState } from 'react'
import { Button, Field, Input, Stack, Textarea } from '@chakra-ui/react'
import type { ApiError, CreateDemandPayload } from '../types'

interface DemandFormProps {
  initialValues?: Partial<CreateDemandPayload>
  isSubmitting?: boolean
  error?: ApiError | null
  onSubmit: (payload: CreateDemandPayload) => void
}

export function DemandForm({
  initialValues,
  isSubmitting = false,
  error,
  onSubmit,
}: DemandFormProps) {
  const [values, setValues] = useState<CreateDemandPayload>({
    serviceRecipient: initialValues?.serviceRecipient ?? '',
    serviceScope: initialValues?.serviceScope ?? '',
    periodStart: initialValues?.periodStart ?? '',
    periodEnd: initialValues?.periodEnd ?? '',
    topic: initialValues?.topic ?? '',
    description: initialValues?.description ?? '',
  })

  function updateField(field: keyof CreateDemandPayload, value: string) {
    setValues(current => ({
      ...current,
      [field]: value,
    }))
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()

    onSubmit({
      serviceRecipient: values.serviceRecipient.trim(),
      serviceScope: values.serviceScope.trim(),
      periodStart: values.periodStart || undefined,
      periodEnd: values.periodEnd || undefined,
      topic: values.topic?.trim() || undefined,
      description: values.description?.trim() || undefined,
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap={5}>
        <Field.Root required invalid={Boolean(error?.fieldErrors?.serviceRecipient)}>
          <Field.Label>Service recipient</Field.Label>
          <Input
            value={values.serviceRecipient}
            onChange={event => updateField('serviceRecipient', event.target.value)}
          />
          <Field.ErrorText>{error?.fieldErrors?.serviceRecipient}</Field.ErrorText>
        </Field.Root>

        <Field.Root required invalid={Boolean(error?.fieldErrors?.serviceScope)}>
          <Field.Label>Service scope</Field.Label>
          <Textarea
            value={values.serviceScope}
            onChange={event => updateField('serviceScope', event.target.value)}
          />
          <Field.ErrorText>{error?.fieldErrors?.serviceScope}</Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={Boolean(error?.fieldErrors?.periodStart)}>
          <Field.Label>Period start</Field.Label>
          <Input
            type="date"
            value={values.periodStart}
            onChange={event => updateField('periodStart', event.target.value)}
          />
          <Field.ErrorText>{error?.fieldErrors?.periodStart}</Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={Boolean(error?.fieldErrors?.periodEnd)}>
          <Field.Label>Period end</Field.Label>
          <Input
            type="date"
            value={values.periodEnd}
            onChange={event => updateField('periodEnd', event.target.value)}
          />
          <Field.ErrorText>{error?.fieldErrors?.periodEnd}</Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={Boolean(error?.fieldErrors?.topic)}>
          <Field.Label>Topic</Field.Label>
          <Input
            value={values.topic}
            onChange={event => updateField('topic', event.target.value)}
          />
          <Field.ErrorText>{error?.fieldErrors?.topic}</Field.ErrorText>
        </Field.Root>

        <Field.Root invalid={Boolean(error?.fieldErrors?.description)}>
          <Field.Label>Description</Field.Label>
          <Textarea
            value={values.description}
            onChange={event => updateField('description', event.target.value)}
          />
          <Field.ErrorText>{error?.fieldErrors?.description}</Field.ErrorText>
        </Field.Root>

        <Button type="submit" colorPalette="blue" loading={isSubmitting} alignSelf="start">
          Save demand
        </Button>
      </Stack>
    </form>
  )
}
